// React i hooks
import { memo, useEffect, useState } from "react";

// Tipovi i konstante
import { LeasingEditProps } from "./types";
import {
  LeasingEditFormValues,
  LeasingType,
} from "services/api/calculations/leasing/types";
import { initialLeasingEditForm } from "services/api/calculations/leasing/consts";

// API servisi
import {
  createLeasingAPI,
  getLeasingAPI,
  updateLeasingAPI,
} from "services/api/calculations/leasing/api";

// UI komponente
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

// Form hooks
import {
  useHandleDropdownChange,
  useHandleNumberFieldChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";

// Validacija
import { validateYup } from "services/forms/validation/validation";
import { CALCULATION_LEASING_EDIT_FORM_SCHEMA } from "./validation";

// Helperi
import { useTranslate } from "helpers";

// Validacija grešaka
import { Errors } from "services/forms/validation/types";

const LeasingEditCard: React.FC<LeasingEditProps> = (props) => {
  const { leasingId, onSuccess } = props;

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<LeasingEditFormValues>(
    initialLeasingEditForm
  );

  useEffect(() => {
    if (leasingId) {
      fetchLeasing(leasingId);
    }
  }, [leasingId]);

  const fetchLeasing = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialLeasingEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getLeasingAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);

    setIsLoading(false);

    setErrors({});
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      CALCULATION_LEASING_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      let result;
      if (!!leasingId) {
        result = await updateLeasingAPI(leasingId, formValues);
      } else {
        result = await createLeasingAPI(formValues);
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues
  );

  const handleNumberFieldChange = useHandleNumberFieldChange(
    setFormValues,
    formValues
  );


  const calculationLeasingTypeItems: DropdownOption[] = Object.entries(
    LeasingType
  ).map(([key, value]) => {
    return {
      value: key.toString(),
      text: value.toString(),
    };
  });
  return (
    <>
      <div className="flex gap-4">
        <div className="w-full m-auto">
          <div className="space-y-3">
            <form noValidate className="text-left">
              <div className="px-5 pb-3 mb-2">
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 ">
                    <TextField
                      type="text"
                      name="name"
                      label={t("admin.calculation.leasing.add.name")}
                      placeholder={t(
                        "admin.calculation.leasing.add.name.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.name}
                      value={formValues.name}
                      required
                      endIcon=""
                    ></TextField>
                    <p className="text-xs my-2 text-slate-600">
                      {t("admin.calculation.leasing.add.name.description")}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 ">
                    <Dropdown
                      name="leasingTypeId"
                      label={t("admin.calculation.leasing.add.leasingtype")}
                      options={calculationLeasingTypeItems}
                      onChange={handleDropdownChange}
                      value={formValues.leasingTypeId}
                      error={errors.leasingTypeId}
                      required
                    />
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 ">
                    <TextField
                      type="number"
                      name="interestRate"
                      label={t("admin.calculation.leasing.add.interestrate")}
                      placeholder={t(
                        "admin.calculation.leasing.add.interestrate.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.interestRate}
                      value={formValues.interestRate}
                      required
                      groupAppend="%"
                    ></TextField>
                  </div>
                </div>
              </div>
              <div className="px-5 pb-5 flex justify-end ">
                <Button
                  variant="primary"
                  size="full"
                  children={t("admin.save")}
                  onClick={handleFormSubmit}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(LeasingEditCard);
